import React from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import {
  User,
  ScrollText,
  Settings as SettingsIcon,
  Webhook,
  Cpu,
  CreditCard,
  Users,
  History,
  Database,
  ListChecks,
  MessageSquare,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { ThemedText } from '../components';
import { ProfileSection } from './ProfilePage';
import { MyCertificatesSection } from './MyCertificatesPage';
import { CertificateDefinitionsSection } from './CertificateDefinitionsPage';
import { NotificationEventsSection } from './NotificationEventsPage';
import { WebhooksSection } from './WebhooksPage';
import { AIUsageSection } from './AIUsagePage';
import { LicenseStatusSection } from './LicenseStatusPage';
import { UsersSection } from './UsersPage';
import { AuditLogSection } from './AuditLogPage';
import { KnowledgeBaseSettingsSection } from './KnowledgeBasePage';
import { FAQsSection } from './FAQsPage';
import { GeneratedReportsSection } from './GeneratedReportsPage';
import { FileManagerSection } from './FileManagerPage';
import PortalSubmissionSetting from '../components/settings/PortalSubmissionSetting';

const SettingsContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  position: sticky;
  top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    position: static;
  }
`;

const RailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RailGroupTitle = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.muted};
  padding: 0 ${({ theme }) => theme.spacing.sm};
  margin-bottom: 4px;
`;

const RailItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm}`};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, theme }) => ($active ? `${theme.colors.tint}1a` : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.tint : theme.colors.text)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  font-size: 0.9rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? `${theme.colors.tint}1a` : theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.tint};
  }
`;

const Pane = styled.div`
  min-width: 0;
`;

const SubTabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  width: fit-content;
`;

const SubTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ $active, theme }) => ($active ? theme.colors.background : 'transparent')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : theme.colors.muted)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ConfigHeading = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const SettingsPage = () => {
  const theme = useTheme();
  const { isAdmin } = useRole();
  const [searchParams, setSearchParams] = useSearchParams();

  const groups = [
    {
      title: 'Account',
      items: [
        { key: 'profile', label: 'Profile', icon: <User size={16} /> },
        { key: 'certificates', label: 'Certificates', icon: <ScrollText size={16} /> },
      ],
    },
    ...(isAdmin()
      ? [
          {
            title: 'Organization',
            items: [
              { key: 'configuration', label: 'Configuration', icon: <SettingsIcon size={16} /> },
              { key: 'users', label: 'Users', icon: <Users size={16} /> },
              { key: 'audit-log', label: 'Audit Log', icon: <History size={16} /> },
            ],
          },
          {
            title: 'Usage',
            items: [
              { key: 'ai-usage', label: 'AI Usage', icon: <Cpu size={16} /> },
              { key: 'license', label: 'License & Usage', icon: <CreditCard size={16} /> },
            ],
          },
          {
            title: 'Integrations',
            items: [
              { key: 'integrations', label: 'Webhooks', icon: <Webhook size={16} /> },
              { key: 'knowledge-base', label: 'Knowledge Base', icon: <Database size={16} /> },
            ],
          },
          {
            title: 'Tools',
            items: [
              { key: 'faqs', label: 'Manage FAQs', icon: <MessageSquare size={16} /> },
              { key: 'reports', label: 'Reports', icon: <FileText size={16} /> },
              { key: 'file-manager', label: 'File Manager', icon: <FolderOpen size={16} /> },
            ],
          },
        ]
      : []),
  ];

  const allKeys = groups.flatMap((g) => g.items.map((i) => i.key));
  const requested = searchParams.get('tab') || 'profile';
  const tab = allKeys.includes(requested) ? requested : 'profile';
  const setTab = (key) => setSearchParams({ tab: key });

  // Certificates pane inner sub-tab (admins only): my certificates vs certificate types.
  // Lives in `?sub=` so it preserves the host `?tab=certificates`.
  const certSub = searchParams.get('sub') === 'types' ? 'types' : 'my';
  const setCertSub = (s) =>
    setSearchParams((prev) => {
      prev.set('tab', 'certificates');
      prev.set('sub', s);
      return prev;
    });

  return (
    <Layout currentPath="/settings">
      <SettingsContainer>
        <PageTitle theme={theme}>Settings</PageTitle>

        <SettingsGrid theme={theme}>
          <Rail theme={theme}>
            {groups.map((g) => (
              <RailGroup key={g.title}>
                <RailGroupTitle theme={theme}>{g.title}</RailGroupTitle>
                {g.items.map((it) => (
                  <RailItem
                    key={it.key}
                    theme={theme}
                    $active={tab === it.key}
                    onClick={() => setTab(it.key)}
                  >
                    {it.icon}
                    {it.label}
                  </RailItem>
                ))}
              </RailGroup>
            ))}
          </Rail>

          <Pane>
            {tab === 'profile' && <ProfileSection />}

            {tab === 'certificates' &&
              (isAdmin() ? (
                <>
                  <SubTabBar theme={theme}>
                    <SubTab
                      theme={theme}
                      $active={certSub === 'my'}
                      onClick={() => setCertSub('my')}
                    >
                      <ScrollText size={14} /> My Certificates
                    </SubTab>
                    <SubTab
                      theme={theme}
                      $active={certSub === 'types'}
                      onClick={() => setCertSub('types')}
                    >
                      <ListChecks size={14} /> Certificate Types
                    </SubTab>
                  </SubTabBar>
                  {certSub === 'types' ? <CertificateDefinitionsSection /> : <MyCertificatesSection />}
                </>
              ) : (
                <MyCertificatesSection />
              ))}

            {tab === 'configuration' && isAdmin() && (
              <div>
                <ConfigHeading theme={theme}>
                  <ThemedText type="subtitle">Configuration</ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Organization-wide settings. Only administrators can change these.
                  </ThemedText>
                </ConfigHeading>
                <PortalSubmissionSetting />
                <NotificationEventsSection />
              </div>
            )}

            {tab === 'users' && isAdmin() && <UsersSection />}
            {tab === 'audit-log' && isAdmin() && <AuditLogSection />}
            {tab === 'ai-usage' && isAdmin() && <AIUsageSection />}
            {tab === 'license' && isAdmin() && <LicenseStatusSection />}
            {tab === 'integrations' && isAdmin() && <WebhooksSection />}
            {tab === 'knowledge-base' && isAdmin() && <KnowledgeBaseSettingsSection />}
            {tab === 'faqs' && isAdmin() && <FAQsSection />}
            {tab === 'reports' && isAdmin() && <GeneratedReportsSection />}
            {tab === 'file-manager' && isAdmin() && <FileManagerSection />}
          </Pane>
        </SettingsGrid>
      </SettingsContainer>
    </Layout>
  );
};

export default SettingsPage;
